/**
 * 04 提交成功 `/enroll/success`
 * -----------------------------------------------------------------------------
 * 即时回执：提交成立后状态为「待缴费」（`PENDING_PAYMENT`，提交那一步是瞬态 `SUBMITTED`）。
 * 版面按设计稿「学员端 · 提交成功」：绿色对勾 → 标题 → 单号卡（可复制）→
 * 报名进度 → 蓝色提示 → 底部两键。
 *
 * ⚠️ 文案只讲「已提交 + 去缴费」，**不出现任何「等待审核 / 核验中」的表述**：
 * 设计稿副标题写的是「系统正在自动核验」，那是核验前移之前的旧流程。
 */
import { Toast } from 'antd-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRegistration } from '../api/hooks';
import { formatCentsText } from '../components/FormRenderer/logic';
import { CheckIcon } from '../components/icons';
import {
  Button,
  Card,
  KV,
  LoadingBlock,
  Notice,
  PageShell,
  ProgressRail,
} from '../components/layout';
import { copyText } from '../lib/clipboard';
import { FLOW_STEP_COUNT, FLOW_STEPS, flowProgress } from '../lib/flow';

export default function EnrollSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationId = searchParams.get('registrationId') ?? undefined;
  const detail = useRegistration(registrationId);

  const id = detail.data?.id ?? registrationId;
  const status = detail.data?.status ?? 'PENDING_PAYMENT';

  /**
   * 离开这一页的唯一正确落点是**首页**。
   *
   * 为什么不用 `navigate(-1)`：这条链路上每一步都是 `replace`
   * （`/enroll/new` → `/enroll/:id` → 本页），历史栈里已经没有「上一步」了 ——
   * 硬退会退到流程外（公众号 / 空白页），而学员想的是「我报完了，回去吧」。
   * 用 `replace` 去首页，本页也不留在栈里：再按返回不会又看见一张回执。
   * 落点写 `/home` 而不是 `/`：后者是一条 `<Navigate to="/home" replace>` 的重定向，
   * 多一跳不说，全站其他「回服务台」的地方（`EnrollNew` / `NotFound` / `Guide`）都用 `/home`。
   */
  const goHome = () => navigate('/home', { replace: true });

  return (
    <PageShell
      layout="result"
      footerRows={3}
      footer={
        <>
          {/* 设计稿页脚是两排 50 全圆按钮：主操作在上。
              第三行是文字链「返回首页」—— 报名到这一页就结束了，「回去」是正常收尾动作，
              而原来页脚只有「去缴费 / 查看报名详情」，两条都通向**缴费链路**，
              学员想离开这条链路时无处可去（2026-10-22 反馈）。 */}
          <Button
            variant="primary"
            block
            className="btn--hero"
            disabled={!id}
            onClick={() => id && navigate(`/payment/${id}`)}
          >
            去缴费
          </Button>
          <Button
            variant="ghost"
            block
            className="btn--hero"
            disabled={!id}
            onClick={() => id && navigate(`/registration/${id}`)}
          >
            查看报名详情
          </Button>
          {/*
            这条**永不禁用**：深链 / 刷新后 `registrationId` 可能拿不到（`id` 为空），
            那时上面两颗按钮都是灰的 —— 如果连「回首页」也需要 id，
            学员就被困在一张什么都点不动的回执上。它是兜底出口，不依赖任何数据。
          */}
          <button type="button" className="page-footer__link" onClick={goHome}>
            返回首页
          </button>
        </>
      }
    >
      <div className="result-hero">
        <span className="result-hero__icon">
          <CheckIcon size={44} strokeWidth={2.4} />
        </span>
        <h2 className="result-hero__title">报名提交成功</h2>
        <p className="result-hero__desc">资料已提交，请按缴费指引完成转账并填写备注码</p>
      </div>

      {/*
        只有「确实在查那张单」时才显示加载。`useRegistration` 在 id 为空时
        `enabled: false`，查询永远停在 pending —— 深链 / 刷新丢掉 `registrationId`
        的话，页面会永远写着「正在加载报名信息」，比报错更让人不知所措。
      */}
      {registrationId && detail.isPending ? <LoadingBlock text="正在加载报名信息" /> : null}

      {!registrationId || detail.error ? (
        <Notice tone="warning">
          回执信息已失效（直接打开或刷新本页会丢单号）。报名已提交成功，可在「我的报名」里查看这张单。
        </Notice>
      ) : null}

      {detail.data ? (
        <>
          <div className="order-card">
            <div>
              <div className="order-card__label">报名单号</div>
              <div className="order-card__no mono-num">{detail.data.registrationNo}</div>
            </div>
            <button
              type="button"
              className="copy-chip"
              onClick={() =>
                void copyText(detail.data?.registrationNo ?? '').then((ok) =>
                  Toast.show({
                    icon: ok ? 'success' : 'fail',
                    content: ok ? '单号已复制' : '复制失败，请手动记录',
                  }),
                )
              }
            >
              复制单号
            </button>
          </div>

          <Card
            title="报名进度"
            hint={`第 ${flowProgress(status).stepNo} / ${FLOW_STEP_COUNT} 步 · 待缴费`}
            className="card--progress"
          >
            <ProgressRail steps={FLOW_STEPS} current={flowProgress(status).railCurrent} />
          </Card>

          <Card title="缴费金额" tight>
            <KV
              label="应缴金额"
              value={
                <span className="mono-num amount-strong">
                  {detail.data.payableCents > 0
                    ? formatCentsText(detail.data.payableCents)
                    : '以缴费指引为准'}
                </span>
              }
              strong
            />
            <KV
              label="转账备注码"
              value={<span className="mono-num">{detail.data.paymentCode ?? '生成中'}</span>}
            />
          </Card>

          <Notice tone="info">
            缴费到账后系统按备注码自动核销，核销完成会推送「报名成功」通知；也可在服务台随时查看。
          </Notice>
        </>
      ) : null}
    </PageShell>
  );
}
